import {
  extractRowsWithErrors,
  getImportCellErrorMessages,
  IMPORT_VALIDATION_FIELD_ALIASES,
  normalizeImportErrorMessages,
  normalizeRowsWithErrors,
} from "helpers/importCellErrors";

/**
 * `/screen-capture` is the current API prefix. The older `/screenshot-imports`
 * and `/trade-capture` prefixes still serve the original endpoints, but the
 * Other surface preview/fill endpoints exist only under `/screen-capture`.
 */
export const SCREEN_CAPTURE_SESSIONS_END_POINT = "/screen-capture/sessions";

/** Saved import UI: checkboxes + audit trail action date (YYYY-MM-DD). */
export const TRADE_CAPTURE_IMPORT_PREFS_KEY = "tradeCapture_importPrefs";

/**
 * Create-session OCR pipeline. Pricer Option Capture defaults to ICS blotter
 * and omits `source` (backend stays on blotter OCR). Other Call/Put Import
 * sends OTHER_CALL_PUT_IMPORT so the same popup reads an ICE Call/Put ladder.
 */
export const TRADE_CAPTURE_SOURCE = {
  ICS_BLOTTER: "ics_blotter",
  OTHER_CALL_PUT_IMPORT: "other_call_put_import",
};

/**
 * What the popup produces. OPTIONS is the Pricer Option Capture flow
 * (validate → import). OTHER_SURFACE reads an option chain screenshot from
 * the Vol Surface → Other Surface page and fills one tenor of the surface
 * (preview → approve / dismiss).
 */
export const TRADE_CAPTURE_OUTPUT = {
  OPTIONS: "options",
  OTHER_SURFACE: "other_surface",
};

/** `screenshot_type` sent with an Other surface create-session request. */
export const OTHER_SURFACE_SCREENSHOT_TYPE = "option_chain";

/** Add-screenshot is only offered while the session can still be edited. */
export const SCREENSHOT_IMPORT_STATUS = {
  DRAFT: "draft",
  VALIDATED: "validated",
};

export const TRADE_CAPTURE_SOURCE_OPTIONS = [
  { value: TRADE_CAPTURE_SOURCE.ICS_BLOTTER, label: "Trades" },
  {
    value: TRADE_CAPTURE_SOURCE.OTHER_CALL_PUT_IMPORT,
    label: "Open Options",
  },
];

export const TRADE_COLUMNS = [
  { key: "option", title: "Option #", width: 70, editable: false },
  { key: "legs", title: "# Legs", width: 70, editable: true },
  { key: "commodity", title: "Commodity", width: 100, editable: true },
  { key: "contract_style", title: "Contract-Style", width: 130, editable: true },
  { key: "type", title: "Type", width: 160, editable: true },
  { key: "ul", title: "UL", width: 80, editable: true },
  { key: "bespoke_tenors", title: "Bespoke Tenors", width: 110, editable: true },
  { key: "tenor", title: "Tenor", width: 120, editable: true },
  { key: "live", title: "Live", width: 60, editable: true },
  { key: "legs_strike", title: "Legs Strike", width: 100, editable: true },
  { key: "legs_putcall", title: "Legs Put/Call", width: 100, editable: true },
  { key: "legs_buysell", title: "Legs Buy/Sell", width: 100, editable: true },
  { key: "legs_ratio", title: "Legs Ratio", width: 90, editable: true },
  { key: "buy_qty", title: "Buy Qty", width: 80, editable: true },
  { key: "buy_percentage", title: "Buy %", width: 70, editable: true },
  { key: "buy", title: "Buy", width: 100, editable: true },
  { key: "sell", title: "Sell", width: 100, editable: true },
  { key: "sell_percentage", title: "Sell %", width: 70, editable: true },
  { key: "sell_qty", title: "Sell Qty", width: 80, editable: true },
  { key: "period_type", title: "Period Type", width: 100, editable: true },
  { key: "average", title: "Average", width: 80, editable: true },
  { key: "cost_of_carry", title: "Cost of Carry", width: 100, editable: true },
  { key: "rf", title: "Rf", width: 60, editable: true },
  // Screenshot clock, shown and stored as UTC. Other Call/Put Import is usually empty.
  {
    key: "actioned_at_time",
    title: "Time (UTC)",
    width: 118,
    editable: true,
    placeholder: "HH:MM",
  },
  { key: "premium_summary", title: "Premium (Summary)", width: 140, editable: false, displayOnly: true },
  { key: "premium_calculated", title: "Premium (Calculated)", width: 200, editable: false, displayOnly: true },
];

/**
 * Other surface ladder grid. C/P and premium follow the UL above the grid,
 * so only strike and the two settles are editable.
 */
export const OTHER_SURFACE_LADDER_COLUMNS = [
  { key: "strike", title: "Strike", width: 110, editable: true },
  { key: "call_settle", title: "Call Settle", width: 110, editable: true },
  { key: "put_settle", title: "Put Settle", width: 110, editable: true },
  { key: "cp", title: "C/P", width: 70, editable: false, displayOnly: true },
  { key: "premium", title: "Premium", width: 110, editable: false, displayOnly: true },
];

/** Per-strike outcome of an Other surface preview. */
export const OTHER_SURFACE_PREVIEW_STATUS = {
  NEW: "new",
  CHANGED: "changed",
  UNCHANGED: "unchanged",
  REMOVED: "removed",
  NO_VOL: "no_vol",
};

export const OTHER_SURFACE_PREVIEW_STATUS_META = {
  [OTHER_SURFACE_PREVIEW_STATUS.NEW]: { label: "New", color: "blue" },
  [OTHER_SURFACE_PREVIEW_STATUS.CHANGED]: { label: "Changed", color: "gold" },
  [OTHER_SURFACE_PREVIEW_STATUS.UNCHANGED]: { label: "Unchanged", color: "default" },
  [OTHER_SURFACE_PREVIEW_STATUS.REMOVED]: { label: "Removed", color: "red" },
  [OTHER_SURFACE_PREVIEW_STATUS.NO_VOL]: { label: "No vol", color: "volcano" },
};

/** @deprecated Prefer IMPORT_VALIDATION_FIELD_ALIASES from helpers/importCellErrors. */
export const TRADE_CAPTURE_VALIDATION_FIELD_ALIASES =
  IMPORT_VALIDATION_FIELD_ALIASES;

export {
  normalizeImportErrorMessages as normalizeTradeCaptureValidationMessages,
};

/**
 * Accepts the canonical `rows_with_errors` array, a legacy keyed object, or
 * an already-normalized list. Returns messages for one cell.
 */
export function getTradeCaptureCellValidationMessages(
  validationErrors,
  rowIndex,
  columnKey,
  { option } = {}
) {
  const rows = normalizeRowsWithErrors(validationErrors);
  const rowOption =
    option != null
      ? option
      : Array.isArray(validationErrors)
        ? validationErrors[rowIndex]?.option ??
          validationErrors[rowIndex]?.row_data?.option ??
          validationErrors[rowIndex]?.rowData?.option
        : null;
  return getImportCellErrorMessages(rows, {
    rowIndex,
    option: rowOption,
    field: columnKey,
  });
}

/** Prefer data.rows_with_errors; fall back to data.validation_errors. */
export function getTradeCaptureRowsWithErrors(responseOrData) {
  return extractRowsWithErrors(responseOrData);
}

export const VALID_TYPES = [
  "Call",
  "Put",
  "Call Calendar",
  "Call Spread",
  "Call Stupid",
  "Conversion (to call)",
  "Conversion (to put)",
  "Diagonal Call Spread",
  "Diagonal Collar (to call)",
  "Diagonal Collar (to put)",
  "Diagonal Put Spread",
  "Put Calendar",
  "Put Spread",
  "Put Stupid",
  "Fence (to call)",
  "Fence (to put)",
  "Straddle",
  "Strangle",
  "Synthetic Futures (to call)",
  "Synthetic Futures (to put)",
  "Call Fly",
  "Call Ladder",
  "Put Fly",
  "Put Ladder",
  "Bespoke",
  "Iron Fly",
  "Put Condor",
  "Call Condor",
  "Call Spread vs Put",
  "Call Spread vs Call",
  "Put Spread vs Put",
  "Put Spread vs Call",
];
